import type { Movie } from "@/types/media";

interface DetailHeroProps {
  movie: Movie;
  isFav: boolean;
  onToggleFav: () => void;
  onShare: () => void;
}

export default function DetailHero({ movie, isFav, onToggleFav, onShare }: DetailHeroProps) {
  const metaRows = [
    { label: "类型", value: movie.genres.join(" / ") },
    { label: "地区", value: movie.region },
    { label: "上映年份", value: String(movie.year) },
    { label: "片长", value: movie.duration },
    { label: "导演", value: movie.director },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-6 md:gap-8">
      <div className="w-full max-w-[260px] md:max-w-none mx-auto md:mx-0">
        <div className="w-full aspect-[2/3] rounded-lg overflow-hidden bg-background-200 border border-background-200">
          <img
            src={movie.cover}
            alt={`${movie.title} 海报`}
            title={`${movie.title} 剧情介绍与演员信息`}
            className="w-full h-full object-cover object-top"
          />
        </div>
      </div>

      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <span className="px-2.5 py-1 rounded-md bg-primary-100 text-primary-700 text-xs font-medium">
            {movie.type}
          </span>
          {movie.isHot && (
            <span className="px-2.5 py-1 rounded-md bg-accent-100 text-accent-700 text-xs font-medium">
              热门
            </span>
          )}
          {movie.isNew && (
            <span className="px-2.5 py-1 rounded-md bg-secondary-100 text-secondary-700 text-xs font-medium">
              新上架
            </span>
          )}
        </div>

        <h1 className="font-heading font-extrabold text-2xl md:text-4xl text-foreground-950">
          {movie.title}
        </h1>

        <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-600">
          <span className="flex items-center gap-1.5">
            <i className="ri-star-fill text-accent-500"></i>
            <span className="font-label font-bold text-foreground-900 text-base">
              {movie.rating.toFixed(1)}
            </span>
            分
          </span>
          <span className="flex items-center gap-1.5">
            <i className="ri-fire-line text-accent-500"></i>
            {movie.views.toLocaleString()} 次浏览
          </span>
        </div>

        <dl className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
          {metaRows.map((row) => (
            <div key={row.label} className="flex gap-3 text-sm">
              <dt className="w-20 shrink-0 text-foreground-400">{row.label}</dt>
              <dd className="text-foreground-800 min-w-0">{row.value}</dd>
            </div>
          ))}
          <div className="flex gap-3 text-sm sm:col-span-2">
            <dt className="w-20 shrink-0 text-foreground-400">主演</dt>
            <dd className="text-foreground-800">{movie.actors.map((a) => a.name).join("、")}</dd>
          </div>
        </dl>

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={onToggleFav}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-label transition-colors cursor-pointer whitespace-nowrap ${
              isFav
                ? "bg-accent-500 text-secondary-950 font-semibold"
                : "border border-background-300 text-foreground-800 hover:bg-background-100"
            }`}
          >
            <i className={isFav ? "ri-heart-3-fill" : "ri-heart-3-line"}></i>
            {isFav ? "已收藏" : "收藏"}
          </button>
          <button
            type="button"
            onClick={onShare}
            className="flex items-center gap-2 px-5 py-2.5 rounded-md border border-background-300 text-foreground-800 text-sm font-label hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-share-forward-line"></i>
            分享
          </button>
          <div className="flex items-center gap-2 text-foreground-400">
            <span className="text-xs">分享到</span>
            <a
              href={`https://mdsix.asia/share/share.php?url=${encodeURIComponent(
                typeof window !== "undefined" ? window.location.href : "",
              )}&title=${encodeURIComponent(movie.title)}`}
              target="_blank"
              rel="nofollow noopener noreferrer"
              aria-label="分享到微博"
              className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 hover:bg-accent-100 hover:text-accent-700 transition-colors"
            >
              <i className="ri-weibo-line"></i>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}